import {
  Box,
  Button,
  Text,
  VStack,
  HStack,
  Badge,
  Spinner,
  Avatar,
  Flex,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import TrackingPageLayout from "components/TrackingPageLayout";
import { useMyStudents, type MyStudent } from "hooks/useTrackingStudents";

function formatPaymentDue(value: number | string | null | undefined) {
  if (value == null) return "—";
  const n = typeof value === "string" ? parseFloat(value) : value;
  return Number.isNaN(n) ? "—" : `${n.toFixed(2)} FCFA`;
}

export default function TrackingParentPage() {
  const navigate = useNavigate();
  const { students, isLoading, error } = useMyStudents();

  return (
    <TrackingPageLayout
      title="Mes élèves"
      action={
        <HStack spacing={2}>
          <Button size="sm" variant="outline" onClick={() => navigate("/tracking/parent/payments")}>
            Paiements
          </Button>
          <Button size="sm" variant="outline" onClick={() => navigate("/tracking/parent/invoices")}>
            Factures
          </Button>
        </HStack>
      }
    >
      {error && (
        <Box mb={4} p={4} bg="red.50" borderRadius="md">
          <Text color="red.700">
            Erreur lors du chargement. Vous devez peut-être être connecté en tant que parent.
          </Text>
        </Box>
      )}

      {isLoading && students.length === 0 ? (
        <Flex justify="center" py={10}>
          <Spinner size="lg" />
        </Flex>
      ) : students.length === 0 ? (
        <Box p={8} textAlign="center" bg="gray.50" borderRadius="md">
          <Text color="gray.600">Aucun élève associé à votre compte.</Text>
        </Box>
      ) : (
        <VStack align="stretch" spacing={4}>
          {(students as MyStudent[]).map((student) => (
            <Box
              key={student.id}
              p={4}
              borderWidth="1px"
              borderRadius="md"
              bg="white"
              shadow="sm"
              _hover={{ shadow: "md" }}
              cursor="pointer"
              onClick={() => navigate(`/tracking/parent/students/${student.id}`)}
            >
              <Flex justify="space-between" align="center" gap={4}>
                <HStack spacing={4}>
                  <Avatar
                    size="md"
                    name={
                      student.first_name || student.last_name
                        ? `${student.first_name ?? ""} ${student.last_name ?? ""}`.trim()
                        : student.username ?? "?"
                    }
                    src={
                      typeof student.profile_picture === "string"
                        ? student.profile_picture
                        : undefined
                    }
                  />
                  <Box>
                    <Text fontWeight="semibold">
                      {student.first_name || student.last_name
                        ? `${student.first_name ?? ""} ${student.last_name ?? ""}`.trim()
                        : student.username ?? "Sans nom"}
                    </Text>
                    {student.institution_name && (
                      <Text fontSize="sm" color="gray.600">
                        {student.institution_name}
                      </Text>
                    )}
                    {student.address_label && (
                      <Text fontSize="xs" color="gray.500">
                        {student.address_label}
                      </Text>
                    )}
                  </Box>
                </HStack>
                <HStack spacing={2}>
                  {student.is_available !== undefined && (
                    <Badge colorScheme={student.is_available ? "green" : "orange"}>
                      {student.is_available ? "Disponible" : "Indisponible"}
                    </Badge>
                  )}
                  {student.has_active_trip && (
                    <Badge colorScheme="blue">Trajet en cours</Badge>
                  )}
                  {student.payment_due != null && Number(student.payment_due) > 0 && (
                    <Badge colorScheme="yellow">
                      Dû: {formatPaymentDue(student.payment_due)}
                    </Badge>
                  )}
                </HStack>
              </Flex>
            </Box>
          ))}
        </VStack>
      )}
    </TrackingPageLayout>
  );
}
